import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LogOut,
  User,
  Mail,
  Shield,
  Lock,
  KeyRound,
  CheckCircle2,
  Eye,
  EyeOff,
  Menu,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useLoadingBar } from '../contexts/LoadingBarContext'
import { Sidebar } from '../components/shared/Sidebar'
import { authFetch, getApiBase } from '../lib/api'

function getInitial(name?: string | null, email?: string | null): string {
  if (name?.trim()) return name.trim().charAt(0).toUpperCase()
  if (email?.trim()) return email.trim().charAt(0).toUpperCase()
  return '?'
}

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const loadingBar = useLoadingBar()

  const handleChangePassword = async (e: React.BaseSyntheticEvent) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess(false)

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match')
      return
    }
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password')
      return
    }

    setPasswordLoading(true)
    loadingBar.start()
    try {
      const res = await authFetch(`${getApiBase()}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setPasswordError(
          (data as { error?: string }).error ?? 'Failed to change password'
        )
        return
      }
      setPasswordSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch {
      setPasswordError('Network error')
    } finally {
      setPasswordLoading(false)
      loadingBar.done()
    }
  }

  return (
    <div className='flex h-screen bg-background text-foreground font-sans overflow-hidden'>
      <Sidebar
        active='profile'
        onNavigate={section => {
          if (section === 'dashboard') navigate('/dashboard')
          else if (section === 'settings') navigate('/settings')
          else if (section === 'profile') navigate('/profile')
        }}
        mobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
      />

      <main className='flex-1 overflow-auto relative min-w-0'>
        {/* Mobile: sticky header with menu */}
        <div className='lg:hidden sticky top-0 z-10 flex items-center gap-3 px-4 py-3 bg-white/95 backdrop-blur border-b border-slate-200/60'>
          <button
            type='button'
            onClick={() => setSidebarOpen(true)}
            className='p-2 rounded-lg hover:bg-slate-100 text-muted-foreground'
            aria-label='Open menu'
          >
            <Menu size={24} />
          </button>
          <span className='font-semibold text-slate-800'>Profile</span>
        </div>

        {/* Gradient hero */}
        <div className='relative h-40 sm:h-48 bg-gradient-to-br from-indigo-100 via-sky-100 to-violet-100 shadow-sm overflow-hidden border-b border-slate-200/60'>
          <div className='absolute inset-0 bg-white/50' />
          <div
            className='absolute inset-0 opacity-[0.10]'
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.35) 1px, transparent 0)',
              backgroundSize: '24px 24px',
            }}
          />
          <div className='relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8'>
            <div className='flex items-end gap-4 sm:gap-6'>
              <div className='flex h-20 w-20 sm:h-24 sm:w-24 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg text-2xl sm:text-3xl font-bold text-white ring-4 ring-white/70'>
                {getInitial(user?.name, user?.email)}
              </div>
              <div className='pb-1 min-w-0'>
                <h1 className='text-xl sm:text-2xl font-bold text-slate-900 truncate'>
                  {user?.name ?? 'User'}
                </h1>
                <p className='text-slate-600 text-xs sm:text-sm mt-0.5 truncate'>
                  {user?.email ?? '—'}
                </p>
                {user?.role && (
                  <span className='inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-slate-900/5 text-slate-700 text-xs font-medium border border-slate-200/70'>
                    <Shield size={12} />
                    {user.role}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10 pb-8 sm:pb-12'>
          {/* Account card */}
          <section className='bg-white rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden mb-4 sm:mb-6'>
            <div className='px-4 sm:px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white'>
              <h2 className='text-base font-semibold text-slate-800 flex items-center gap-2'>
                <span className='flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600'>
                  <User size={18} />
                </span>{' '}
                Account details
              </h2>
            </div>
            <dl className='divide-y divide-slate-100'>
              <div className='flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4'>
                <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600'>
                  <Mail size={20} />
                </span>
                <div className='min-w-0 flex-1'>
                  <dt className='text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Email
                  </dt>
                  <dd className='text-slate-900 font-medium mt-0.5'>
                    {user?.email ?? '—'}
                  </dd>
                </div>
              </div>
              <div className='flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4'>
                <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600'>
                  <User size={20} />
                </span>
                <div className='min-w-0 flex-1'>
                  <dt className='text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Name
                  </dt>
                  <dd className='text-slate-900 font-medium mt-0.5'>
                    {user?.name ?? '—'}
                  </dd>
                </div>
              </div>
              {user?.role && (
                <div className='flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4'>
                  <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600'>
                    <Shield size={20} />
                  </span>
                  <div className='min-w-0 flex-1'>
                    <dt className='text-xs font-medium text-slate-500 uppercase tracking-wider'>
                      Role
                    </dt>
                    <dd className='text-slate-900 font-medium mt-0.5 capitalize'>
                      {user.role}
                    </dd>
                  </div>
                </div>
              )}
            </dl>
          </section>

          {/* Change password – inline form */}
          <section className='bg-white rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden mb-4 sm:mb-6'>
            <div className='px-4 sm:px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white'>
              <h2 className='text-base font-semibold text-slate-800 flex items-center gap-2'>
                <span className='flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-600'>
                  <KeyRound size={18} />
                </span>{' '}
                Change password
              </h2>
              <p className='text-sm text-slate-500 mt-1'>
                Use a strong password with at least 6 characters.
              </p>
            </div>
            <form
              onSubmit={handleChangePassword}
              className='p-4 sm:p-6 space-y-4'
            >
              {passwordError && (
                <div className='flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-100'>
                  <Lock size={16} className='shrink-0' />
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className='flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 text-emerald-700 text-sm border border-emerald-100'>
                  <CheckCircle2 size={16} className='shrink-0' />
                  Password updated successfully.
                </div>
              )}
              <div>
                <label
                  htmlFor='profile-current-password'
                  className='block text-sm font-medium text-slate-700 mb-1.5'
                >
                  Current password
                </label>
                <div className='relative'>
                  <input
                    id='profile-current-password'
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    required
                    autoComplete='current-password'
                    className='w-full px-4 pr-12 py-2.5 rounded-xl border border-slate-300 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors'
                  />
                  <button
                    type='button'
                    onClick={() => setShowCurrentPassword(v => !v)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 transition-colors'
                    aria-label={
                      showCurrentPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showCurrentPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='profile-new-password'
                    className='block text-sm font-medium text-slate-700 mb-1.5'
                  >
                    New password
                  </label>
                  <div className='relative'>
                    <input
                      id='profile-new-password'
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      autoComplete='new-password'
                      className='w-full px-4 pr-12 py-2.5 rounded-xl border border-slate-300 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors'
                    />
                    <button
                      type='button'
                      onClick={() => setShowNewPassword(v => !v)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 transition-colors'
                      aria-label={
                        showNewPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showNewPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  <p className='mt-1 text-xs text-slate-500'>
                    Min. 6 characters
                  </p>
                </div>
                <div>
                  <label
                    htmlFor='profile-confirm-password'
                    className='block text-sm font-medium text-slate-700 mb-1.5'
                  >
                    Confirm new password
                  </label>
                  <div className='relative'>
                    <input
                      id='profile-confirm-password'
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      autoComplete='new-password'
                      className='w-full px-4 pr-12 py-2.5 rounded-xl border border-slate-300 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors'
                    />
                    <button
                      type='button'
                      onClick={() => setShowConfirmPassword(v => !v)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 transition-colors'
                      aria-label={
                        showConfirmPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div className='pt-2'>
                <button
                  type='submit'
                  disabled={passwordLoading}
                  className='inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium shadow-md hover:from-blue-500 hover:to-indigo-500 hover:shadow-lg disabled:opacity-60 transition-all'
                >
                  <Lock size={18} />
                  {passwordLoading ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </form>
          </section>

          {/* Sign out */}
          <section className='bg-white rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden'>
            <div className='px-6 py-4 border-b border-slate-100'>
              <h2 className='text-base font-semibold text-slate-800'>
                Sign out
              </h2>
              <p className='text-sm text-slate-500 mt-0.5'>
                Sign out of your account on this device.
              </p>
            </div>
            <div className='p-6'>
              <button
                type='button'
                onClick={() => {
                  logout()
                  navigate('/login')
                }}
                className='inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-slate-200 text-slate-700 font-medium hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-all'
              >
                <LogOut size={18} />
                Sign out
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}

export default ProfilePage
